import { useState } from 'react';
import { trackEvent } from '../lib/analytics';

// Set-builder (plan 4.4): help beginners naar de juiste starterset. Prelaunch-veilig:
// beveelt een SET-TYPE + route aan, geen prijzen/voorraad/leverbeloftes.
type Players = '2' | '4' | 'familie' | 'vereniging';
type Place = 'binnen' | 'buiten' | 'beide';
type Use = 'recreatief' | 'intensief';

const playerOpts: Array<{ v: Players; label: string }> = [
  { v: '2', label: '2 spelers' },
  { v: '4', label: '4 spelers' },
  { v: 'familie', label: 'Familie / recreatie' },
  { v: 'vereniging', label: 'Vereniging / school' }
];
const placeOpts: Array<{ v: Place; label: string }> = [
  { v: 'binnen', label: 'Binnen (zaal)' },
  { v: 'buiten', label: 'Buiten' },
  { v: 'beide', label: 'Beide' }
];
const useOpts: Array<{ v: Use; label: string }> = [
  { v: 'recreatief', label: 'Recreatief' },
  { v: 'intensief', label: 'Intensief / vaak' }
];

function recommend(players: Players, place: Place, use: Use): { title: string; body: string } {
  const ball = place === 'binnen' ? 'indoor ballen' : place === 'buiten' ? 'outdoor ballen' : 'indoor- en outdoor ballen';
  const net = players === 'vereniging' ? 'stevige of mobiele netten' : 'een draagbaar net';
  if (players === 'vereniging') {
    return { title: 'Vereniging- of schoolpakket', body: `Voor groepen adviseren we een groter pakket met meerdere paddles, ${ball} en ${net}. Vraag een pakketadvies of offerte aan voor het juiste aantal.` };
  }
  const size = players === '2' ? '2-speler starterset' : players === '4' ? '4-speler starterset' : 'familie-starterset';
  const paddleTip = use === 'intensief' ? 'iets steviger paddles' : 'controle-paddles die vergevingsgezind zijn';
  return { title: size, body: `Kies een ${size} met ${paddleTip}, ${ball} en ${net}. Zo heb je in een keer alles om te starten.` };
}

export function SetBuilder() {
  const [players, setPlayers] = useState<Players | null>(null);
  const [place, setPlace] = useState<Place | null>(null);
  const [use, setUse] = useState<Use | null>(null);
  const done = players && place && use;
  const rec = done ? recommend(players!, place!, use!) : null;

  const Group = <T extends string>(props: { legend: string; opts: Array<{ v: T; label: string }>; value: T | null; onChange: (v: T) => void }) => (
    <fieldset className="setBuilderStep">
      <legend>{props.legend}</legend>
      <div className="setBuilderOpts">
        {props.opts.map((o) => (
          <button
            type="button"
            key={o.v}
            className={props.value === o.v ? 'setBuilderOpt active' : 'setBuilderOpt'}
            aria-pressed={props.value === o.v}
            onClick={() => props.onChange(o.v)}
          >{o.label}</button>
        ))}
      </div>
    </fieldset>
  );

  return (
    <section className="section setBuilder" id="starterset-samenstellen" aria-label="Starterset samenstellen">
      <div className="sectionHeading compactHeading">
        <p className="eyebrow">Starterset samenstellen</p>
        <h2>Welke starterset past bij jou?</h2>
        <p className="sectionIntro">Beantwoord 3 vragen en we adviseren het juiste type set. Kopen kan zodra de eerste batch klaar is.</p>
      </div>
      <div className="setBuilderGrid">
        <Group legend="1. Aantal spelers" opts={playerOpts} value={players} onChange={setPlayers} />
        <Group legend="2. Binnen of buiten" opts={placeOpts} value={place} onChange={setPlace} />
        <Group legend="3. Gebruik" opts={useOpts} value={use} onChange={setUse} />
      </div>
      {rec ? (
        <div className="setBuilderResult" role="status">
          <p className="eyebrow">Ons advies</p>
          <h3>{rec.title}</h3>
          <p>{rec.body}</p>
          <div className="ctaRow inlineCtaRow">
            <a className="primaryCta" href="/collecties/startersets/" onClick={() => trackEvent({ event: 'cta_click', location: 'set_builder', label: rec.title, routeFamily: 'products', placement: 'result' })}>Bekijk startersets</a>
            <a className="secondaryDarkCta" href="/keuzehulp/">Naar de keuzehulp</a>
          </div>
        </div>
      ) : (
        <p className="setBuilderHint">Kies hierboven je situatie voor een advies op maat.</p>
      )}
    </section>
  );
}
